import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, Trash2, CheckCircle2, AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { plaidApi } from '../../services/api';
import BankLinkButton from './BankLinkButton';
import LoadingSpinner from '../UI/LoadingSpinner';
import { TENANT } from '../../config';
import { SETTINGS_PANEL } from '../settings/ui';
interface BankAccount {
  id: string;
  plaid_account_id?: string;
  institution_name: string;
  account_name?: string;
  last4: string | null;
  account_type: string;
  subtype?: string;
  status: 'active' | 'inactive' | 'pending';
  created_at?: string;
}

const BankAccountsManager: React.FC = () => {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [accountBalances, setAccountBalances] = useState<Map<string, any>>(new Map());
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);

  const fetchBalances = useCallback(async (accountsToFetch: BankAccount[]) => {
    const balancePromises = accountsToFetch.map(async (account) => {
      try {
        const balanceRes = await plaidApi.getAccountBalance(account.id);
        if (balanceRes.data && balanceRes.data.success && balanceRes.data.data) {
          return { accountId: account.id, balance: balanceRes.data.data.balance };
        }
      } catch (err) {
        console.warn(`Failed to fetch balance for account ${account.id}:`, err);
      }
      return null;
    });

    const balances = await Promise.all(balancePromises);
    const balanceMap = new Map();
    balances.forEach(b => {
      if (b) balanceMap.set(b.accountId, b.balance);
    });
    setAccountBalances(balanceMap);
  }, []);

  /**
   * Optimized balance fetching using Plaid batch endpoint
   * Reduces N+1 API calls to a single batch request
   */
  const fetchBalancesOptimized = useCallback(async (accountsToFetch?: BankAccount[]) => {
    try {
      const response = await plaidApi.getAllBalances();

      if (response.data && response.data.success && response.data.data) {
        const { accounts: balanceData } = response.data.data;
        const balanceMap = new Map();

        balanceData.forEach(item => {
          if (item.success && item.balance) {
            balanceMap.set(item.accountId, item.balance);
          }
        });

        setAccountBalances(balanceMap);
        setLastRefreshed(new Date());
        console.log(`✅ Plaid batch fetched ${balanceMap.size} balances`);
      }
    } catch (error) {
      console.error('Failed to fetch Plaid balances:', error);
      // Fall back to individual fetches if batch fails
      await fetchBalances(accountsToFetch || []);
    }
  }, [fetchBalances]);

  const loadBankAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await plaidApi.getAccounts();

      if (response.data && response.data.success && response.data.data) {
        const accountsData = response.data.data.accounts || [];
        // Map Plaid response to BankAccount interface
        const mappedAccounts = accountsData.map((acc: any) => ({
          id: acc.id,
          plaid_account_id: acc.plaid_account_id,
          institution_name: acc.institution_name,
          account_name: acc.account_name,
          last4: acc.last4,
          account_type: acc.account_type,
          subtype: acc.subtype,
          status: acc.status || 'active',
          created_at: acc.created_at,
        }));
        setAccounts(mappedAccounts);
        setError(null);

        // Fetch balances for all accounts using batch endpoint
        await fetchBalancesOptimized(mappedAccounts);
      }
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
      setError('Could not load your bank accounts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchBalancesOptimized]);

  useEffect(() => {
    loadBankAccounts();
  }, [loadBankAccounts]);

  // Auto-refresh balances every 5 minutes when enabled
  useEffect(() => {
    if (!autoRefreshEnabled || accounts.length === 0) return;

    const intervalId = setInterval(() => {
      console.log('🔄 Auto-refreshing bank balances...');
      fetchBalancesOptimized();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(intervalId);
  }, [autoRefreshEnabled, accounts.length, fetchBalancesOptimized]);

  const handleBankLinked = () => {
    setError(null);
    // Reload all accounts to get the latest data
    loadBankAccounts();
  };

  const handleRefreshBalances = async () => {
    setIsRefreshing(true);
    try {
      await fetchBalancesOptimized();
      setError(null);
    } catch (error) {
      setError('Could not refresh balances. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRemoveAccount = async (accountId: string) => {
    if (!window.confirm('Are you sure you want to remove this bank account?')) {
      return;
    }

    try {
      const response = await plaidApi.removeAccount(accountId);

      if (response.data && response.data.success) {
        setAccounts(accounts.filter(acc => acc.id !== accountId));
        // Remove balance from map
        const newBalances = new Map(accountBalances);
        newBalances.delete(accountId);
        setAccountBalances(newBalances);
        setError(null);
      } else {
        throw new Error('Failed to remove account');
      }
    } catch (error: any) {
      console.error('Failed to remove bank account:', error);
      setError(error.response?.data?.message || error.message || 'Could not remove the bank account.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const base = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';
    switch (status) {
      case 'active':
        return (
          <span className={`${base} bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]`}>
            <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Active
          </span>
        );
      case 'pending':
        return (
          <span className={`${base} bg-secondary text-muted-foreground`}>
            <AlertCircle className="h-3 w-3" aria-hidden="true" /> Pending
          </span>
        );
      default:
        return <span className={`${base} bg-secondary text-muted-foreground`}>Inactive</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Toolbar (only with accounts) */}
      {accounts.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {lastRefreshed && (
              <p className="text-xs text-muted-foreground">Last updated {lastRefreshed.toLocaleTimeString()}</p>
            )}
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={autoRefreshEnabled}
                onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <span className="text-xs text-muted-foreground">Auto-refresh (every 5 min)</span>
            </label>
          </div>
          <button
            onClick={handleRefreshBalances}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-foreground transition-colors md:hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh balances
          </button>
        </div>
      )}

      {/* Accounts List */}
      {accounts.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-border bg-secondary/50 py-12 text-center">
          <Landmark className="mx-auto mb-4 h-14 w-14 text-muted-foreground" aria-hidden="true" />
          <h3 className="mb-2 text-lg font-semibold text-foreground">No bank accounts</h3>
          <p className="mb-6 text-sm text-muted-foreground">
            Link your bank account to enable ACH transfers for investments
          </p>
          <div className="flex flex-col items-center">
            <BankLinkButton onLinked={handleBankLinked} variant="primary" />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {accounts.map((account) => (
            <div key={account.id} className={`${SETTINGS_PANEL} p-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-4">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-secondary">
                      <Landmark className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-foreground">
                          {account.institution_name || 'Bank Account'}
                        </h3>
                        {getStatusBadge(account.status)}
                      </div>

                      <p className="mt-1 text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Account type:</span>{' '}
                        <span className="capitalize">{account.account_type}</span>
                        {account.last4 && ` · ••••${account.last4}`}
                      </p>
                      {account.created_at && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Linked on {new Date(account.created_at).toLocaleDateString()}
                        </p>
                      )}

                      {/* Balance Information */}
                      {accountBalances.has(account.id) && (
                        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-border pt-4 sm:grid-cols-2 sm:gap-4">
                          {accountBalances.get(account.id)?.available != null && (
                            <div className="min-w-0">
                              <p className="text-xs text-muted-foreground">Available balance</p>
                              <p className="text-lg font-semibold tabular-nums text-[hsl(var(--success))]">
                                {formatCurrency(accountBalances.get(account.id).available)}
                              </p>
                            </div>
                          )}
                          {accountBalances.get(account.id)?.current != null && (
                            <div className="min-w-0">
                              <p className="text-xs text-muted-foreground">Current balance</p>
                              <p className="text-lg font-semibold tabular-nums text-foreground">
                                {formatCurrency(accountBalances.get(account.id).current)}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <button
                    onClick={() => handleRemoveAccount(account.id)}
                    className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-md text-destructive transition-colors md:h-9 md:w-9 md:hover:bg-destructive/10"
                    title="Remove account"
                    aria-label="Remove account"
                  >
                    <Trash2 className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}

          {/* Add Another Account Button */}
          <div className="pt-2">
            <BankLinkButton onLinked={handleBankLinked} variant="secondary" />
          </div>
        </div>
      )}

      {/* About — collapsible */}
      <div className={`${SETTINGS_PANEL} overflow-hidden`}>
        <button
          type="button"
          onClick={() => setAboutOpen((o) => !o)}
          aria-expanded={aboutOpen}
          className="flex w-full items-center justify-between gap-3 p-4 text-left"
        >
          <span className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-sm font-medium text-foreground">About bank connections</span>
          </span>
          {aboutOpen
            ? <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            : <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />}
        </button>
        {aboutOpen && (
          <div className="border-t border-border p-4 rise-in">
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Bank accounts are securely connected through our Plaid partnership</li>
              <li>• We never store your bank login credentials — Plaid uses bank-level encryption</li>
              <li>• Plaid only shares account and routing numbers needed for ACH transfers</li>
              <li>• Balances are refreshed automatically when viewing</li>
              <li>• ACH transfers typically take 2-3 business days</li>
              <li>• You can link multiple accounts for flexibility</li>
              <li>• You can revoke {TENANT.branding.name} access to your bank at any time</li>
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              <strong className="font-semibold text-foreground">Your rights:</strong> Under Plaid's consumer protections, you have the right to know what data is shared,
              control which accounts are connected, and disconnect at any time. Plaid is SOC 2 Type II certified and uses
              256-bit encryption.{' '}
              <a href="https://plaid.com/legal/#consumers" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700">
                Learn more about Plaid's privacy policy →
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankAccountsManager;
